import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { useProperty } from "../lib/property-context";
import { useImpersonatedUnitId } from "../lib/view-as-context";
import { inputCls, textareaCls } from "./form-controls";
import { UnitScopeField, type UnitScope } from "./UnitScopeField";

interface FaqEntry {
  _id: string;
  question: string;
  answer: string;
  sortOrder: number;
  unitId: string | null;
  unitLabel: string | null;
}

// Landlord-curated FAQ. Tenants read only entries scoped to their
// unit or property-wide; landlords see all. Edit ability gated on
// `realRole` so view-as-tenant hides the controls.
export function FaqSection({ realRole }: { realRole: "landlord" | "tenant" | null | undefined }) {
  const property = useProperty();
  const entries = useQuery(
    anyApi.faq.listForProperty,
    property ? { propertySlug: property.slug } : "skip",
  ) as FaqEntry[] | undefined;
  const add = useMutation(anyApi.faq.add);
  const removeEntry = useMutation(anyApi.faq.remove);

  // Landlord-as-tenant impersonation: filter to entries scoped to
  // the impersonated unit (plus property-wide). No-op otherwise.
  const impersonatedUnitId = useImpersonatedUnitId();
  const visibleEntries = useMemo(() => {
    if (!entries || impersonatedUnitId === null) return entries;
    return entries.filter(
      (e) => !e.unitId || e.unitId === impersonatedUnitId,
    );
  }, [entries, impersonatedUnitId]);

  const isLandlord = realRole === "landlord";
  const [adding, setAdding] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState("");
  const [newScope, setNewScope] = useState<UnitScope>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!property) return;
    setSubmitting(true);
    setError(null);
    try {
      await add({
        propertySlug: property.slug,
        question: newQuestion,
        answer: newAnswer,
        unitId: newScope || undefined,
      });
      setNewQuestion("");
      setNewAnswer("");
      setNewScope(undefined);
      setAdding(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (entryId: string, question: string) => {
    if (!confirm(`Delete "${question}"?`)) return;
    try {
      await removeEntry({ entryId });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <header className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">FAQ</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Wifi, trash, parking, and other property quirks.
          </p>
        </div>
        {isLandlord && !adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="shrink-0 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 transition-colors"
          >
            Add entry
          </button>
        )}
      </header>

      <Body
        entries={visibleEntries}
        isLandlord={isLandlord}
        onRemove={handleRemove}
      />

      {adding && (
        <form
          onSubmit={handleAdd}
          className="px-6 py-4 border-t border-slate-100 space-y-3"
        >
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Question
            </label>
            <input
              type="text"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              required
              autoFocus
              placeholder="e.g. What day is trash pickup?"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Answer
            </label>
            <textarea
              value={newAnswer}
              onChange={(e) => setNewAnswer(e.target.value)}
              required
              rows={3}
              placeholder="e.g. Wednesday morning. Bin to the curb by 7am."
              className={`${textareaCls} resize-none`}
            />
          </div>
          <UnitScopeField value={newScope} onChange={setNewScope} />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setNewQuestion("");
                setNewAnswer("");
                setNewScope(undefined);
                setError(null);
              }}
              disabled={submitting}
              className="text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !newQuestion.trim() || !newAnswer.trim()}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {submitting ? "Saving…" : "Add"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function Body({
  entries,
  isLandlord,
  onRemove,
}: {
  entries: FaqEntry[] | undefined;
  isLandlord: boolean;
  onRemove: (id: string, question: string) => void;
}) {
  if (entries === undefined) {
    return <p className="px-6 py-4 text-sm text-slate-500">Loading…</p>;
  }
  if (entries.length === 0) {
    return (
      <div className="px-6 py-8 text-center">
        <p className="text-sm font-medium text-slate-900">
          {isLandlord ? "No entries yet." : "No FAQ entries yet."}
        </p>
        <p className="mt-1 text-sm text-slate-500">
          {isLandlord
            ? "Add the questions tenants ask most often — wifi password, trash day, etc."
            : "Your landlord will add property quirks here."}
        </p>
      </div>
    );
  }
  return (
    <ul className="px-6 py-4 divide-y divide-slate-100">
      {entries.map((entry) => (
        <Entry
          key={entry._id}
          entry={entry}
          isLandlord={isLandlord}
          onRemove={onRemove}
        />
      ))}
    </ul>
  );
}

function ScopeBadge({ entry }: { entry: FaqEntry }) {
  if (entry.unitLabel) {
    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-600">
        {entry.unitLabel}
      </span>
    );
  }
  return null;
}

function Entry({
  entry,
  isLandlord,
  onRemove,
}: {
  entry: FaqEntry;
  isLandlord: boolean;
  onRemove: (id: string, question: string) => void;
}) {
  const update = useMutation(anyApi.faq.update);
  const [editing, setEditing] = useState(false);
  const [question, setQuestion] = useState(entry.question);
  const [answer, setAnswer] = useState(entry.answer);
  const [scope, setScope] = useState<UnitScope>(entry.unitId ?? undefined);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await update({
        entryId: entry._id,
        question,
        answer,
        // null clears (= property-wide); a unit id sets the scope.
        unitId: scope ?? null,
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (editing) {
    return (
      <li className="py-4 first:pt-0">
        <form onSubmit={handleSave} className="space-y-2">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            required
            className={inputCls}
          />
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            required
            rows={3}
            className={`${textareaCls} resize-none`}
          />
          <UnitScopeField value={scope} onChange={setScope} />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setQuestion(entry.question);
                setAnswer(entry.answer);
                setScope(entry.unitId ?? undefined);
                setError(null);
              }}
              disabled={submitting}
              className="text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !question.trim() || !answer.trim()}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {submitting ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </li>
    );
  }

  return (
    <li className="py-4 first:pt-0 group">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-slate-900">
              {entry.question}
            </p>
            <ScopeBadge entry={entry} />
          </div>
        </div>
        {isLandlord && (
          <div className="shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs font-medium text-slate-500 hover:text-slate-900 px-2 py-1 rounded hover:bg-slate-100"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => onRemove(entry._id, entry.question)}
              className="text-xs font-medium text-slate-500 hover:text-red-600 px-2 py-1 rounded hover:bg-slate-100"
            >
              Delete
            </button>
          </div>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
        {entry.answer}
      </p>
    </li>
  );
}
